import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {
  faAngleDown,
  faAngleRight,
  faAngleUp,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, FEATURES, PLAYLIST_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { usePlaylist } from '@components/Playlists/usePlaylist';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType, PlaylistNewReleasesLikedFragmentDoc } from '@graphql/reactQuery';
import type { Maybe, PlaylistNewReleasesFragmentDoc } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PlaylistPreviewFragmentDoc } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { webappUriPath } from '@utils/url';
import { PreviewHeader } from './Header';
import { PreviewReleases } from './Releases';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistPreview on Shelf {
    id
    name
    releaseCount
    user {
      id
      username
    }
    webappUri
    ...PreviewHeader
  }

  fragment PlaylistNewReleases on ReleasesAddedToShelfAggregate {
    releasesAdded {
      id
      release {
        id
        track {
          id
        }
      }
      ...PreviewPlaylistRow
    }
  }

  fragment PlaylistNewReleasesLiked on UserLikedSongsAggregate {
    releasesLiked {
      id
      release {
        id
        track {
          id
        }
      }
      ...PreviewPlaylistRow
    }
  }
`);

export const MAX_MINIMIZED_RELEASES = 4;

export const PlaylistPreview = ({
  playlistFrag,
  newPlaylist = false,
  ...props
}: {
  playlistFrag: FragmentType<PlaylistPreviewFragmentDoc>;
  newPlaylist?: boolean;
} & (
  | {
      newReleasesFrag?: Maybe<FragmentType<PlaylistNewReleasesFragmentDoc>>;
    }
  | {
      newLikedReleasesFrag?: Maybe<FragmentType<PlaylistNewReleasesLikedFragmentDoc>>;
    }
)) => {
  const logError = useLogError();
  const playlist = getFragment(PlaylistPreviewFragmentDoc, playlistFrag);

  const { id, releaseCount, user, webappUri } = playlist;

  const { authUser } = useAuth();
  const isOwner = authUser?.id === user.id;

  const { releases, trackIDs, isLoading, isFetchingNextPage, hasNextPage, loadMore, error } =
    usePlaylist({
      playlistId: id,
      releaseCount,
      limit: MAX_MINIMIZED_RELEASES,
      dateSort: 'DESC',
      type: 'previewPlaylistRow',
    });

  useEffect(() => {
    if (!error) return;
    logError({
      error,
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Error loading data for playlist preview',
      action: PLAYLIST_ERROR_ACTIONS.PLAYLIST_PREVIEW_FETCH_ERROR,
      errorType: ERROR_TYPE.FETCH_GQL_ERROR,
      level: 'error',
      indexedTags: {
        playlistId: id,
      },
    });
  }, [error, id, logError]);

  const newAddedReleases = 'newReleasesFrag' in props ? props.newReleasesFrag : undefined;
  const newLikedReleases = 'newLikedReleasesFrag' in props ? props.newLikedReleasesFrag : undefined;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const showViewMore = releaseCount > MAX_MINIMIZED_RELEASES;
  const { tablet } = useMediaQuery();

  return (
    <div className="group/preview relative flex max-h-screen transform-gpu flex-col overflow-hidden rounded-lg bg-white shadow-tertiary">
      <PreviewHeader
        playlistFrag={playlist}
        newPlaylist={newPlaylist}
        trackIds={trackIDs}
        isOwner={isOwner}
        disablePlay={releaseCount === 0}
        playlistUrl={webappUri}
      />
      <div
        className={clsx(
          'relative flex h-full w-full flex-col overflow-hidden',
          isExpanded ? 'max-h-[576px]' : 'max-h-[296px]',
        )}
      >
        {releaseCount > 0 ? (
          <PreviewReleases
            releases={releases}
            hasNextPage={hasNextPage}
            loading={isLoading || isFetchingNextPage}
            newReleasesFrag={newAddedReleases}
            newLikedReleasesFrag={newLikedReleases}
            username={user.username}
            releaseCount={releaseCount}
            loadMore={loadMore}
            playlistId={id}
            isExpanded={isExpanded}
          />
        ) : (
          <p className="flex h-14 items-center justify-center text-base-m text-base600">
            There are no sounds in here.
          </p>
        )}
      </div>
      {showViewMore && (
        <div className="h-[38px] border-t border-base100 bg-white text-base-m text-base500 hover:cursor-pointer hover:text-base800">
          {tablet ? (
            <button
              className="flex h-full w-full items-center justify-center gap-[6px] font-medium"
              onClick={() => {
                if (!isExpanded) loadMore();
                setIsExpanded(prev => !prev);
              }}
            >
              View {isExpanded ? 'less' : 'more'}
              <FontAwesomeIcon icon={isExpanded ? faAngleUp : faAngleDown} />
            </button>
          ) : (
            <AccessibleLink
              className="flex h-full w-full items-center justify-center gap-[6px] font-medium"
              pathnameUri={webappUriPath(playlist.webappUri)}
            >
              View all {releaseCount} sound{releaseCount > 1 && 's'}
              <FontAwesomeIcon icon={faAngleRight} />
            </AccessibleLink>
          )}
        </div>
      )}
    </div>
  );
};
